import {ListView} from '@github-ui/list-view'
import {ListViewDensityToggle} from '@github-ui/list-view/ListViewDensityToggle'
import {ListViewMetadata} from '@github-ui/list-view/ListViewMetadata'
import {Spinner} from '@primer/react'
import type {ComponentProps, MutableRefObject} from 'react'

import type {RepositoryItem} from '../types/repository-item'
import {getDisplayReposCount} from '../utils/screen-reader-message'
import type {OrderOptions} from '../utils/sorting-options'
import styles from './ReposList.module.css'
import {ReposListItem} from './ReposListItem'
import type {DirectionRestrictedKeys} from './SortingDropdown'
import {SortingDropdown} from './SortingDropdown'

export type ListViewVariant = ComponentProps<typeof ListView>['variant']

interface ReposListProps {
  query?: string
  showNameWithOwner?: boolean
  compactMode: boolean
  onCompactModeChanged: (value: boolean) => void
  showSpinner: boolean
  repositoryCount: number
  repos: RepositoryItem[]
  sortingItemSelected?: string
  onSortingItemSelect: (key: string) => void
  defaultSortingKey: string
  sortingOptions: Record<string, OrderOptions>
  noReposMessage: React.ReactElement
  directionRestrictedKeys?: DirectionRestrictedKeys
  listRef?: MutableRefObject<HTMLUListElement | undefined>
}

export function ReposList({
  query,
  showNameWithOwner = false,
  compactMode,
  onCompactModeChanged,
  showSpinner,
  repositoryCount,
  repos,
  sortingItemSelected,
  onSortingItemSelect,
  defaultSortingKey,
  sortingOptions,
  noReposMessage,
  directionRestrictedKeys,
  listRef,
}: ReposListProps) {
  const metadata = (
    <ListViewMetadata
      title={<span className={styles.ReposCountText}>{getDisplayReposCount(repositoryCount)}</span>}
      densityToggle={<ListViewDensityToggle />}
    >
      {showSpinner && <Spinner size="small" />}
      <SortingDropdown
        sortingItemSelected={sortingItemSelected || ''}
        onSortingItemSelect={onSortingItemSelect}
        defaultSortingKey={defaultSortingKey}
        sortingOptions={sortingOptions}
        directionRestrictedKeys={directionRestrictedKeys}
      />
    </ListViewMetadata>
  )

  return (
    <div className={styles.ReposListContainer} data-listview-repos-list>
      <ListView
        onVariantChange={value => onCompactModeChanged(value === 'compact')}
        variant={compactMode ? 'compact' : 'default'}
        metadata={metadata}
        title="Repositories list"
        titleHeaderTag="h2"
        listRef={listRef}
      >
        {repos.length > 0 ? (
          repos.map(repo => (
            <ReposListItem
              key={`${repo.owner}/${repo.name}`}
              repo={repo}
              showNameWithOwner={showNameWithOwner}
              query={query}
            />
          ))
        ) : (
          // A ListView is a <ul> element, so it only accepts <li> children. This component is a workaround to allow
          // Blankslate to be used as a child of a ListView. We don't need to add any additional styles to the <li> element
          // because the ListView already ensures that the <li> element is styled correctly.
          <li>{noReposMessage}</li>
        )}
      </ListView>
    </div>
  )
}

try{ ReposList.displayName ||= 'ReposList' } catch {}